import React from 'react';
import { motion } from 'framer-motion';
import { Award, MapPin, Layers, Brain } from 'lucide-react';

const features = [
  {
    id: 'experience',
    icon: Award,
    title: '10+ Years of Experience',
    description: 'A decade of delivering successful software solutions across diverse industries and technologies.',
  },
  {
    id: 'local',
    icon: MapPin,
    title: 'Miami-Based Team',
    description: 'Local presence means better communication, collaboration, and understanding of your business needs.',
  },
  {
    id: 'end-to-end',
    icon: Layers,
    title: 'End-to-End Development',
    description: 'From concept to deployment and beyond. We handle design, development, testing, and maintenance.',
  },
  {
    id: 'ai',
    icon: Brain,
    title: 'AI & Automation Expertise',
    description: 'Leverage cutting-edge AI and automation to give your business a competitive advantage.',
  },
];

const WhyChooseUs = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="why-us"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="why-choose-us-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030305] via-[#0a0a12] to-[#030305]" />
      
      {/* Decorative gradient */}
      <div className="absolute top-1/2 left-0 w-1/2 h-1/2 bg-violet-600/10 rounded-full blur-[200px] -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-cyan-400 text-sm font-mono tracking-wider uppercase mb-4 block">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-testid="why-us-heading">
              Your Trusted Software Company <span className="gradient-text">Near Me</span> in Miami
            </h2>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-8">
              Choosing the right Miami tech company is crucial for your project's success. 
              We combine deep technical expertise with a genuine understanding of your business goals 
              to deliver solutions that drive real results.
            </p>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-gray-300 text-sm">100% US-Based</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-gray-300 text-sm">NDA Protected</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-gray-300 text-sm">Agile Methodology</span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Features Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.id}
                  variants={itemVariants}
                  className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 hover:bg-white/5 hover:border-violet-500/30 transition-all duration-300 group"
                  data-testid={`feature-card-${feature.id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-violet-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
